import React from "react";

import DailyReadPlayer from "@app/analytics/components/DailyReadPlayer";
import useDailyRead from "@app/analytics/hooks/recordings/useDailyRead";
import NoteModule from "@app/analytics/Pulse/components/NoteModule";
import {
  Content,
  LeftColumn,
  RightColumn,
} from "@app/analytics/Pulse/components/styles";
import TopicsTable from "@app/analytics/Pulse/components/TopicsTable";
import useForYou from "@app/analytics/Pulse/hooks/useForYou";
import usePulseClips from "@app/analytics/Pulse/hooks/usePulseClips";
import usePulseStore from "@app/analytics/Pulse/stores/pulse";
import { useAuthenticated } from "@app/common/auth/useAuth";
import { getGreetingTime } from "@app/common/utils";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import Stack from "@rui/Stack";

import NoAccess from "./components/NoAccess";
import Summary from "./components/Summary";
import WhatYouMissed from "./components/WhatYouMissed";

const ForYou = () => {
  usePageTitle("For You");

  const {
    isLoading: resultsLoading,
    forYouQuery,
    whatYouMissedQuery,
    hasPermissionError,
  } = useForYou();

  const { metadata: dailyReadMetadata } = useDailyRead();

  const { isInitialLoading: isClipsLoading } = usePulseClips("for-you");

  const selectedRange = usePulseStore((state) => state.forYou.selectedRange);

  const { user } = useAuthenticated();

  const greeting = `Good ${getGreetingTime()}${
    user.firstName ? `, ${user.firstName}` : ""
  }`;

  const forYouData = forYouQuery.data;
  const whatYouMissedData = whatYouMissedQuery.data;

  const isInitialLoading = resultsLoading || isClipsLoading;

  if (hasPermissionError) {
    return <NoAccess type="forYou" />;
  }

  return (
    <Content direction={{ lg: "row" }} sx={{ position: "relative" }}>
      <LeftColumn>
        <Summary
          title={greeting}
          subtitle={`Here's what happened ${selectedRange.label}`}
          content={forYouData?.summary}
          thumbnail={{ src: forYouData?.thumbnailUrl }}
          loading={isInitialLoading}
        />
        <TopicsTable
          topics={forYouData?.pulseTopics ?? []}
          loading={isInitialLoading}
        />
        <WhatYouMissed
          loading={isInitialLoading}
          topics={whatYouMissedData?.pulseTopics ?? []}
          selectedRange={selectedRange.value}
        />
      </LeftColumn>
      <RightColumn>
        {dailyReadMetadata && (
          <DailyReadPlayer
            displayOnly
            forCurrentUser
            metadata={dailyReadMetadata}
            shareable
          />
        )}
        <Stack spacing={2} direction={{ xxs: "row", lg: "column" }}>
          <NoteModule
            type="action_items"
            content={forYouData?.actionItems}
            loading={isInitialLoading}
          />
          <NoteModule
            type="key_questions"
            content={forYouData?.keyQuestions}
            loading={isInitialLoading}
          />
        </Stack>
      </RightColumn>
    </Content>
  );
};

export default ForYou;
